import React, { useState, useEffect, useRef } from 'react';
import ModelsAPI from '../../api/models';
import { 
  ImageProvider, 
  DEFAULT_IMAGE_MODEL,
  IMAGE_PROVIDERS,
  providerRequiresModel,
} from '../../api/structs/providers';
import { AIModel, formatPrice, ModelConstraintsResponse } from '../../api/structs/model';
import { MediaOutputSpec } from '../../api/structs/media-spec';
import { Select, ModelPickerSheet } from '../ui';
import ModelSettingsModal from './ModelSettingsModal';

interface ImageProviderSelectorProps {
  provider: ImageProvider;
  model: string;
  onProviderChange: (provider: ImageProvider) => void;
  onModelChange: (model: string) => void;
  settings: Partial<MediaOutputSpec>;
  onSettingsChange: (settings: Partial<MediaOutputSpec>) => void;
  /** Names for the three controls, for a screen that mounts several selectors. */
  providerAriaLabel?: string;
  modelAriaLabel?: string;
  settingsAriaLabel?: string;
}

const ImageProviderSelector: React.FC<ImageProviderSelectorProps> = ({
  provider,
  model,
  onProviderChange,
  onModelChange,
  settings,
  onSettingsChange,
  providerAriaLabel,
  modelAriaLabel,
  settingsAriaLabel,
}) => {
  const [models, setModels] = useState<AIModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [constraints, setConstraints] = useState<ModelConstraintsResponse | undefined>();
  const [showSettings, setShowSettings] = useState(false);

  // Which provider the current `models` were fetched for. After a provider
  // switch the list is the previous provider's until the new one answers, and
  // the first-model pick below must never read it.
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  useEffect(() => {
    setModels([]);
    setLoadedFor(null);
    if (!providerRequiresModel(provider)) {
      return;
    }

    // A provider switch abandons the request in flight, so a slow answer can
    // never repopulate the list that belongs to the provider now selected.
    let cancelled = false;
    const fetchModels = async () => {
      setLoading(true);
      try {
        const imageModels = await ModelsAPI.getImageModels(provider);
        if (cancelled) return;
        setModels(imageModels);
        setLoadedFor(provider);
      } catch (error) {
        console.error('Failed to fetch models:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchModels();
    return () => {
      cancelled = true;
    };
  }, [provider]);

  useEffect(() => {
    if (!providerRequiresModel(provider) || loadedFor !== provider) {
      return;
    }

    if (!model && models.length > 0) {
      onModelChange(models[0]?.id || DEFAULT_IMAGE_MODEL);
    }
  }, [provider, model, models, loadedFor, onModelChange]);

  // Reset settings when the model changes (clears stale fields from the previous
  // model) — but not on mount, or opening a panel would wipe the settings it was
  // handed and a plain Save would then store the loss.
  const previousModel = useRef(model);
  useEffect(() => {
    if (previousModel.current === model) return;
    previousModel.current = model;
    onSettingsChange({});
  }, [model, onSettingsChange]);

  // Pre-fetch constraints so modal opens instantly
  useEffect(() => {
    if (!providerRequiresModel(provider) || !model) {
      setConstraints(undefined);
      return;
    }

    ModelsAPI.getModelConstraints(model, 'image')
      .then(setConstraints)
      .catch(() => setConstraints(undefined));
  }, [provider, model]);

  // A model the catalog does not list (or a catalog that could not be read) is
  // still the one in use: show it, rather than "Select model" over a saved choice.
  const unlisted =
    model && !loading && !models.some((m) => m.id === model)
      ? [{ value: model, label: model, sublabel: 'not in the current catalog', rightLabel: '' }]
      : [];
  const dropdownOptions = [
    ...unlisted,
    ...models.map((m) => ({
      value: m.id,
      label: m.name,
      sublabel: m.runware?.architecture || m.id,
      rightLabel: formatPrice(m),
    })),
  ];

  return (
    <>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 min-w-0">
        <div className="w-full sm:w-[9rem] sm:flex-shrink-0">
          <Select
            options={IMAGE_PROVIDERS}
            value={provider}
            onChange={(e) => onProviderChange(e.target.value as ImageProvider)}
            selectSize="sm"
            className="w-full min-w-0"
            aria-label={providerAriaLabel}
          />
        </div>

        {providerRequiresModel(provider) && (
          <div className="w-full sm:flex-1">
            <ModelPickerSheet
              options={dropdownOptions}
              value={model}
              onChange={onModelChange}
              placeholder="Select model"
              searchable
              loading={loading}
              chipClassName="text-xs"
              ariaLabel={modelAriaLabel}
            />
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowSettings(true)}
          className="btn btn-sm btn-ghost sm:flex-shrink-0"
          title="Image model settings"
          aria-label={settingsAriaLabel}
        >
          Settings
        </button>
      </div>

      <ModelSettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        modelId={model}
        modality="image"
        settings={settings}
        onSettingsChange={onSettingsChange}
        constraints={constraints}
      />
    </>
  );
};

export default ImageProviderSelector;
